import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HandCoins, Briefcase, CalendarDays, CheckCircle2, ArrowUpRight, Plus, Activity, Clock } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatAED, formatDateTime, countdown, relativeTime } from "../lib/format";
import Avatar from "../components/Avatar";
import PushSuggestionBanner from "../components/PushSuggestionBanner";

function MetricCard({ label, value, accent, icon: Icon, link, sub }) {
  const inner = (
    <div className="ts-card p-6 h-full transition-transform duration-300 hover:-translate-y-0.5" data-testid={`metric-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-start justify-between">
        <div className="overline">{label}</div>
        <Icon size={18} className="text-[#C9A84C]" strokeWidth={1.5} />
      </div>
      <div className="mt-4 font-serif text-4xl text-[#F0EDE8] tracking-tight">{value}</div>
      {sub && <div className="mt-2 text-xs text-[#9A9590]">{sub}</div>}
      {accent && (
        <div className="mt-3 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.2em] text-[#C9A84C]">
          {accent} <ArrowUpRight size={12} />
        </div>
      )}
    </div>
  );
  return link ? <Link to={link}>{inner}</Link> : inner;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Dashboard · The Synergy Table";
    api.get("/dashboard/summary").then((r) => {
      setData(r.data);
      setLoading(false);
    });
  }, []);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const firstName = user?.name?.split(" ")[0];
  const isAdminGeneric = firstName === "Group" || firstName === "Admin";
  const displayName = isAdminGeneric ? "Organizer" : firstName;

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      <PushSuggestionBanner />
      {/* Hero */}
      <div
        className="ts-card relative overflow-hidden p-8 sm:p-12"
        style={{
          background:
            "linear-gradient(135deg, #131313 0%, #1A1A1A 60%), radial-gradient(circle at top right, rgba(201,168,76,0.18), transparent 65%)",
        }}
      >
        <div className="absolute inset-0 grain-overlay opacity-40" />
        <div className="relative">
          <div className="overline">{greeting}</div>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#F0EDE8] mt-3 tracking-tight">
            {displayName || "Welcome"}.
          </h1>
          <p className="text-[#B8B3AC] mt-4 max-w-lg leading-relaxed">
            Here is the rhythm of the table this week — open opportunities, active deals,
            and what needs your attention.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/opportunities/new" className="btn-gold px-5 py-2.5 rounded-md inline-flex items-center gap-2 text-sm" data-testid="quick-post-opp">
              <Plus size={16} /> Post an Opportunity
            </Link>
            <Link to="/deals" className="btn-ghost-gold px-5 py-2.5 rounded-md inline-flex items-center gap-2 text-sm" data-testid="quick-view-deals">
              <HandCoins size={16} /> Update a Deal
            </Link>
          </div>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <MetricCard
          label="Active Deals"
          value={loading ? "—" : data?.active_deals ?? 0}
          accent="In motion"
          icon={HandCoins}
          link="/deals"
          sub="Across all sectors"
        />
        <MetricCard
          label="Open Opportunities"
          value={loading ? "—" : data?.open_opportunities ?? 0}
          accent="Pipeline"
          icon={Briefcase}
          link="/opportunities"
          sub="Awaiting movement"
        />
        <MetricCard
          label="Next Meeting"
          value={loading ? "—" : data?.next_meeting ? countdown(data.next_meeting.date) : "—"}
          icon={CalendarDays}
          link={data?.next_meeting ? `/meetings/${data.next_meeting.id}` : "/meetings"}
          sub={data?.next_meeting ? data.next_meeting.title : "No upcoming meeting"}
        />
        <MetricCard
          label="My Pending Actions"
          value={loading ? "—" : data?.pending_actions ?? 0}
          accent={data?.pending_actions ? "Needs attention" : "All clear"}
          icon={CheckCircle2}
          link="/meetings"
          sub="Assigned to you"
        />
      </div>

      {/* Recent activity + My actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 ts-card p-7">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="overline">Recent Activity</div>
              <h2 className="font-serif text-2xl text-[#F0EDE8] mt-1">Around the table</h2>
            </div>
            <Activity size={18} className="text-[#C9A84C]" strokeWidth={1.5} />
          </div>
          <ul className="space-y-4" data-testid="activity-feed">
            {(data?.activity || []).map((a, i) => (
              <li key={`${a.timestamp}-${a.link || i}`} className="flex items-start gap-4 group">
                <div className="w-2 h-2 rounded-full bg-[#C9A84C] mt-2 shrink-0" />
                <Link to={a.link} className="flex-1 min-w-0">
                  <div className="text-sm text-[#F0EDE8] group-hover:text-[#C9A84C] transition">
                    {a.title}
                  </div>
                  <div className="text-xs text-[#9A9590] mt-0.5">
                    by {a.actor} · {relativeTime(a.timestamp)}
                  </div>
                </Link>
              </li>
            ))}
            {(!data?.activity || data.activity.length === 0) && (
              loading ? (
                [0, 1, 2].map((i) => (
                  <li key={i} className="flex items-start gap-4 animate-pulse">
                    <div className="w-2 h-2 rounded-full bg-[#C9A84C]/30 mt-2 shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 bg-[#C9A84C]/10 rounded w-3/4" />
                      <div className="h-2 bg-[#C9A84C]/10 rounded w-1/3" />
                    </div>
                  </li>
                ))
              ) : (
                <li className="text-sm text-[#B8B3AC]">No activity yet. The table is quiet — for now.</li>
              )
            )}
          </ul>
        </div>

        <div className="ts-card p-7">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="overline">Your List</div>
              <h2 className="font-serif text-2xl text-[#F0EDE8] mt-1">Action items</h2>
            </div>
            <Clock size={18} className="text-[#C9A84C]" strokeWidth={1.5} />
          </div>
          <ul className="space-y-3" data-testid="my-actions">
            {(data?.my_actions || []).map((a) => (
              <li key={a.id} className="border-b border-[#C9A84C]/10 pb-3 last:border-0">
                <Link to={`/meetings/${a.meeting_id}`} className="block">
                  <div className="text-sm text-[#F0EDE8] hover:text-[#C9A84C] transition">{a.title}</div>
                  <div className="text-[11px] text-[#9A9590] mt-1 uppercase tracking-widest">
                    {a.meeting_title}
                  </div>
                </Link>
              </li>
            ))}
            {(!data?.my_actions || data.my_actions.length === 0) && (
              <li className="text-sm text-[#9A9590]">No pending actions. Nicely done.</li>
            )}
          </ul>
        </div>
      </div>

      {/* Next meeting banner */}
      {data?.next_meeting && (
        <Link
          to={`/meetings/${data.next_meeting.id}`}
          className="ts-card p-7 sm:p-8 flex flex-col sm:flex-row sm:items-center gap-6 hover:border-[#C9A84C]/55 transition"
          data-testid="next-meeting-banner"
        >
          <div className="flex-1">
            <div className="overline">Upcoming</div>
            <div className="font-serif text-2xl sm:text-3xl text-[#F0EDE8] mt-2">{data.next_meeting.title}</div>
            <div className="text-sm text-[#9A9590] mt-2">{formatDateTime(data.next_meeting.date)} · {data.next_meeting.location || "Location TBD"}</div>
          </div>
          <div className="flex -space-x-2">
            {(data.next_meeting.attendees || []).slice(0, 5).map((a) => (
              <Avatar key={a.id} name={a.name} photoUrl={a.photo_url} color={a.avatar_color} size={36} className="ring-2 ring-[#1A1A1A]" />
            ))}
            {data.next_meeting.attendees?.length > 5 && (
              <div className="w-9 h-9 rounded-full bg-[#1A1A1A] gold-border flex items-center justify-center text-xs text-[#C9A84C]">
                +{data.next_meeting.attendees.length - 5}
              </div>
            )}
          </div>
          <div className="text-2xl font-serif text-[#C9A84C]">{countdown(data.next_meeting.date)}</div>
        </Link>
      )}
    </div>
  );
}
